import React, { ReactNode, useEffect, useState, useCallback, useMemo } from 'react';
import { IdleWarningModal } from 'components/IdleWarningModal';
import { useIdleTimer } from 'hooks/useIdleTimer';
import { useKeycloak } from 'providers/KeycloakProvider';
import { keycloakService } from 'services/keycloakService';

interface IdleLogoutProviderProps {
    children: ReactNode;
    timeoutMinutes?: number;
    warningMinutes?: number;
    crossTab?: boolean;
}

const MINUTE_MS = 60 * 1000;
const DEFAULT_TIMEOUT_MINUTES = 15;
const DEFAULT_WARNING_MINUTES = 1;

export const IdleLogoutProvider: React.FC<IdleLogoutProviderProps> = ({
    children,
    timeoutMinutes = DEFAULT_TIMEOUT_MINUTES,
    warningMinutes = DEFAULT_WARNING_MINUTES,
    crossTab = true,
}) => {
    const { authenticated } = useKeycloak();
    const [showWarning, setShowWarning] = useState(false);
    const [warningRemainingTime, setWarningRemainingTime] = useState(0);

    const timeout = useMemo(() => timeoutMinutes * MINUTE_MS, [timeoutMinutes]);
    const promptTimeout = useMemo(() => warningMinutes * MINUTE_MS, [warningMinutes]);

    const handleIdle = useCallback(() => {
        if (authenticated) {
            // Auto logout when idle
            keycloakService.onAuthLogout();
        }
    }, [authenticated]);

    const handlePrompt = useCallback(() => {
        if (authenticated) {
            setShowWarning(true);
        }
    }, [authenticated]);

    const handleActive = useCallback(() => {
        setShowWarning(false);
    }, []);

    const idleTimerOptions = useMemo(() => ({
        events: [
            'mousedown',
            'keypress',
            'click',
        ],
        timeout,
        promptTimeout,
        onIdle: handleIdle,
        onPrompt: handlePrompt,
        onActive: handleActive,
        startOnMount: authenticated,
        crossTab,
        lockOnPrompt: true, 
    }), [timeout, promptTimeout, handleIdle, handlePrompt, handleActive, authenticated, crossTab]);

    const {
        reset,
        getTimeRemaining,
        pause,
        resume
    } = useIdleTimer(idleTimerOptions);

    // Update warning remaining time
    useEffect(() => {
        if (!showWarning) return;

        const updateRemainingTime = () => {
            const remaining = getTimeRemaining();
            setWarningRemainingTime(remaining);
        };

        updateRemainingTime();
        const interval = setInterval(updateRemainingTime, 1000);

        return () => clearInterval(interval);
    }, [showWarning, getTimeRemaining]);

    // Pause timer when not authenticated
    useEffect(() => {
        if (authenticated) {
            resume();
        } else {
            pause();
            setShowWarning(false);
        }
    }, [authenticated, pause, resume]);

    const handleContinueSession = () => {
        setShowWarning(false);
        reset();
    };

    const handleLogoutNow = () => {
        setShowWarning(false);
        keycloakService.onAuthLogout();
    };

    return (
        <>
            {children}
            <IdleWarningModal
                isOpen={showWarning && authenticated}
                remainingTime={warningRemainingTime}
                onContinue={handleContinueSession}
                onLogout={handleLogoutNow}
            />
        </>
    );
};
